import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { api, COLORS, MODULES } from "@/lib/api";
import { usd, money } from "@/lib/currency";
import { downloadFile } from "@/lib/api";
import { PageHeader, Panel, Kpi, ChartTooltip, Segmented, Loading } from "@/components/dash";
import { DataTable } from "@/components/DataTable";

const grid = <CartesianGrid stroke={COLORS.grid} vertical={false} />;

export default function AdminUsage() {
  const [days, setDays] = useState("30");
  const { data } = useQuery({ queryKey: ["usage", days], queryFn: async () => (await api.get("/admin/usage", { params: { days } })).data });
  const { data: orders = [] } = useQuery({ queryKey: ["admin-orders"], queryFn: async () => (await api.get("/admin/orders")).data });
  if (!data) return <Loading />;
  const t = data.totals;
  const views = data.by_event.find((e) => e._id === "dashboard_view")?.n || 0;
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="CMS · Usage" title="Usage & Revenue">
        <Segmented testId="usage-days" value={days} onChange={setDays} options={[{ value: "7", label: "7d" }, { value: "30", label: "30d" }, { value: "90", label: "90d" }, { value: "365", label: "1y" }]} />
      </PageHeader>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Kpi testId="usage-kpi-users" label="Registered users" value={t.users} />
        <Kpi testId="usage-kpi-subs" label="Active subscriptions" value={t.active_subs} accent={COLORS.cyan} />
        <Kpi testId="usage-kpi-views" label={`Dashboard views · ${days}d`} value={views} accent={COLORS.blue} />
        <Kpi testId="usage-kpi-orders" label="Paid orders (all time)" value={t.paid_orders} accent={COLORS.orange} />
        <Kpi testId="usage-kpi-revenue" label={`Revenue · ${days}d`} value={usd(t.revenue_usd)} accent={COLORS.orange} />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Daily activity" sub="All tracked events" testId="usage-daily-chart">
          <ResponsiveContainer width="100%" height={240}><AreaChart data={data.daily}>{grid}<XAxis dataKey="_id" stroke="#334155" minTickGap={20} /><YAxis stroke="#334155" allowDecimals={false} />
            <Tooltip content={<ChartTooltip />} /><Area dataKey="n" name="Events" stroke={COLORS.blue} fill={COLORS.blue} fillOpacity={0.3} /></AreaChart></ResponsiveContainer>
        </Panel>
        <Panel title="Revenue" sub="USD per day" testId="usage-revenue-chart">
          <ResponsiveContainer width="100%" height={240}><BarChart data={data.revenue}>{grid}<XAxis dataKey="_id" stroke="#334155" /><YAxis stroke="#334155" />
            <Tooltip content={<ChartTooltip unit="USD" />} /><Bar dataKey="usd" name="Revenue" fill={COLORS.orange} radius={[3, 3, 0, 0]} /></BarChart></ResponsiveContainer>
        </Panel>
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <Panel title="Views by module">
          {data.by_module.map((m) => <div key={m._id} className="flex justify-between text-sm py-1.5 border-b border-slate-800/60"><span>{MODULES.find((x) => x.id === m._id)?.label || m._id}</span><span className="font-mono">{m.n}</span></div>)}
        </Panel>
        <Panel title="Top products">
          {data.top_products.map((m) => <div key={m._id} className="flex justify-between text-sm py-1.5 border-b border-slate-800/60"><span>{m._id}</span><span className="font-mono">{m.n}</span></div>)}
        </Panel>
        <Panel title="Most active users">
          {data.top_users.map((m) => <div key={m._id} className="flex justify-between text-sm py-1.5 border-b border-slate-800/60"><span className="truncate pr-2">{m._id}</span><span className="font-mono">{m.n}</span></div>)}
        </Panel>
      </div>
      <Panel title="Orders">
        <DataTable testId="usage-orders-table" rows={orders} columns={[{ key: "created_at", label: "Date", render: (o) => o.created_at.slice(0, 10) }, { key: "email", label: "Customer" },
          { key: "description", label: "Item" }, { key: "amount_usd", label: "USD", num: true, render: (o) => usd(o.amount_usd) }, { key: "amount_local", label: "Charged", num: true, render: (o) => (o.currency ? money(o.amount_local, o.currency) : "—") }, { key: "status", label: "Status" }, { key: "invoice_no", label: "Invoice", render: (o) => o.status === "paid" ? <button className="text-xs underline text-[#03AB9E]" onClick={() => downloadFile(`/orders/${o.id}/invoice`, "invoice.pdf")}>{o.invoice_no || "Download"}</button> : "—" }]} />
      </Panel>
      <Panel title="Recent activity">
        <DataTable testId="usage-recent-table" rows={data.recent} columns={[{ key: "at", label: "When", render: (r) => r.at.slice(0, 16).replace("T", " ") }, { key: "email", label: "User" },
          { key: "event", label: "Event" }, { key: "module", label: "Module" }, { key: "product", label: "Product" }]} />
      </Panel>
    </div>
  );
}
